/**
 * Canonical institutional identity for ITaaS Suite (PEP) / ITaaS RDC.
 * Single source for NAP, legal display facts, and Organization JSON-LD.
 *
 * Do not invent values. Historical Kinshasa address must never be used as current.
 */
export const ITAAS_INSTITUTIONAL_WEBSITE = "https://www.itaas-rdc.com";
export const ITAAS_SUITE_WEBSITE = "https://www.itaas-suite.com";
/** Stable Schema.org @id for the publisher Organization. */
export const ITAAS_ORGANIZATION_ID = `${ITAAS_INSTITUTIONAL_WEBSITE}/#organization`;
/** Historical address — never use as current public NAP. */
export const ITAAS_HISTORICAL_ADDRESS_KINSHASA = {
    streetAddress: "25, avenue Mont Carmel",
    addressLocality: "Kinshasa",
    addressRegion: "Limete",
    addressCountry: "CD",
    status: "HISTORICAL",
};
export const ITAAS_PUBLIC_EMAIL = "contact@itaas-rdc.com";
export const ITAAS_PUBLIC_PHONE = "+243 830 676 007";
export const ITAAS_PUBLIC_PHONE_TEL = "+243830676007";
export const ITAAS_CURRENT_ADDRESS = {
    streetAddress: "4933, avenue de la Révolution",
    addressLocality: "Lubumbashi",
    addressRegion: "Kampemba",
    addressCountry: "CD",
    displayLine: "4933, avenue de la Révolution, Commune de Kampemba, Lubumbashi, RDC",
    displayLines: [
        "4933, avenue de la Révolution",
        "Commune de Kampemba",
        "Lubumbashi",
        "République démocratique du Congo",
    ],
};
/**
 * Legal identifiers — operator baseline for PEP-2.
 * NIF is intentionally not published (no corresponding official proof in repo).
 */
export const ITAAS_LEGAL_IDENTIFIERS = [
    {
        key: "RCCM",
        label: "RCCM",
        value: "CD/KNM/RCCM/26-B-02850",
        verificationStatus: "VERIFIED",
        public: true,
        source: "PEP-2 operator baseline (2026-07-26)",
    },
    {
        key: "ID_NAT",
        label: "ID. NAT.",
        value: "01-G4701-N61355E",
        verificationStatus: "VERIFIED",
        public: true,
        source: "PEP-2 operator baseline (2026-07-26)",
    },
    {
        key: "INPP",
        label: "INPP",
        value: "14900.00",
        verificationStatus: "VERIFIED",
        public: true,
        source: "PEP-2 operator baseline (2026-07-26)",
    },
    {
        key: "ONEM",
        label: "ONEM",
        value: "021800A26",
        verificationStatus: "VERIFIED",
        public: true,
        source: "PEP-2 operator baseline (2026-07-26)",
    },
];
/** Operational leadership (public). Not statutory directors. */
export const ITAAS_OPERATIONAL_LEADERSHIP = [
    { name: "John MWANZA", titleFr: "CEO", titleEn: "CEO", roleKind: "ROLE_OPERATIONNEL" },
    {
        name: "Benjamin ILUNGA",
        titleFr: "Directeur Général",
        titleEn: "Managing Director",
        roleKind: "ROLE_OPERATIONNEL",
    },
    {
        name: "Elie MONGA",
        titleFr: "Directeur Administratif & Financier",
        titleEn: "Administrative & Finance Director",
        roleKind: "ROLE_OPERATIONNEL",
    },
    {
        name: "Nicolette SANGWA",
        titleFr: "Directrice des Systèmes d'Information",
        titleEn: "Chief Information Officer",
        roleKind: "ROLE_OPERATIONNEL",
    },
    {
        name: "Victor KABANGE",
        titleFr: "Directeur Commercial & Relations Clients",
        titleEn: "Commercial & Customer Relations Director",
        roleKind: "ROLE_OPERATIONNEL",
    },
    {
        name: "Seraphin TSHAMBINASHA",
        titleFr: "Responsable Support & Opérations Techniques",
        titleEn: "Support & Technical Operations Lead",
        roleKind: "ROLE_OPERATIONNEL",
    },
    {
        name: "Remy TSHIMANGA",
        titleFr: "Responsable Commercial — Kinshasa",
        titleEn: "Commercial Lead — Kinshasa",
        roleKind: "ROLE_OPERATIONNEL",
    },
    {
        name: "Samuel KAPUKU",
        titleFr: "Responsable Formation & Adoption",
        titleEn: "Training & Adoption Lead",
        roleKind: "ROLE_OPERATIONNEL",
    },
];
export const ITAAS_INSTITUTION = {
    brandName: "ITaaS RDC",
    legalName: "INFORMATION TECHNOLOGY AS A SERVICE RDC SARL",
    productBrand: "ITaaS Suite",
    relationshipFr: "ITaaS Suite est le portefeuille de solutions logicielles édité et opéré par ITaaS RDC.",
    relationshipEn: "ITaaS Suite is the software product portfolio published and operated by ITaaS RDC.",
    email: ITAAS_PUBLIC_EMAIL,
    telephone: ITAAS_PUBLIC_PHONE,
    telephoneTel: ITAAS_PUBLIC_PHONE_TEL,
    institutionalWebsite: ITAAS_INSTITUTIONAL_WEBSITE,
    productWebsite: ITAAS_SUITE_WEBSITE,
    organizationId: ITAAS_ORGANIZATION_ID,
    address: ITAAS_CURRENT_ADDRESS,
    legalIdentifiers: ITAAS_LEGAL_IDENTIFIERS,
    leadership: ITAAS_OPERATIONAL_LEADERSHIP,
    /** Official social profiles — empty until verified URLs are provided. */
    sameAs: [],
};
/** Organization JSON-LD (Schema.org) — publisher identity. */
export function buildItaasOrganizationJsonLd() {
    const a = ITAAS_INSTITUTION.address;
    return {
        "@context": "https://schema.org",
        "@type": "Organization",
        "@id": ITAAS_ORGANIZATION_ID,
        name: ITAAS_INSTITUTION.brandName,
        legalName: ITAAS_INSTITUTION.legalName,
        url: ITAAS_INSTITUTION.institutionalWebsite,
        email: ITAAS_INSTITUTION.email,
        telephone: ITAAS_INSTITUTION.telephone,
        address: {
            "@type": "PostalAddress",
            streetAddress: a.streetAddress,
            addressLocality: a.addressLocality,
            addressRegion: a.addressRegion,
            addressCountry: a.addressCountry,
        },
        brand: {
            "@type": "Brand",
            name: ITAAS_INSTITUTION.productBrand,
            url: ITAAS_INSTITUTION.productWebsite,
        },
        ...(ITAAS_INSTITUTION.sameAs.length > 0 ? { sameAs: [...ITAAS_INSTITUTION.sameAs] } : {}),
    };
}
/** WebSite JSON-LD for the PEP, published by ITaaS RDC. */
export function buildPepWebSiteJsonLd() {
    return {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "@id": `${ITAAS_SUITE_WEBSITE}/#website`,
        name: ITAAS_INSTITUTION.productBrand,
        url: ITAAS_SUITE_WEBSITE,
        publisher: { "@id": ITAAS_ORGANIZATION_ID },
        inLanguage: ["fr", "en"],
        potentialAction: {
            "@type": "SearchAction",
            target: {
                "@type": "EntryPoint",
                urlTemplate: `${ITAAS_SUITE_WEBSITE}/search?q={search_term_string}`,
            },
            "query-input": "required name=search_term_string",
        },
    };
}
export function publicLegalIdentifierLines() {
    return ITAAS_LEGAL_IDENTIFIERS.filter((i) => i.public && i.verificationStatus === "VERIFIED").map((i) => `${i.label} : ${i.value}`);
}
